import { useState, useEffect } from 'react'
import { Plus, Trash2, Download, Receipt, Loader } from 'lucide-react'
import apiClient from '../../api/axiosClient'
import ReceiptEditor from './ReceiptEditor'

const GenerateReceipt = ({ patient }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  
  const [formData, setFormData] = useState({
    receipt_date: new Date().toISOString().split('T')[0],
    receipt_time: new Date().toTimeString().slice(0, 5),
    items: [{ sl_no: 1, particular: 'OP', amount: '' }],
    narration: ''
  })

  useEffect(() => {
    if (patient) {
      setFormData(prev => ({
        ...prev,
        narration: `${patient.uhid_no || ''} ${patient.full_name || ''}`.trim()
      }))
    }
  }, [patient])

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { 
        sl_no: prev.items.length + 1, 
        particular: '', 
        amount: '' 
      }]
    }))
  }

  const removeItem = (index) => {
    if (formData.items.length === 1) return
    
    const newItems = formData.items.filter((_, i) => i !== index)
    // Renumber items
    newItems.forEach((item, idx) => {
      item.sl_no = idx + 1
    })
    
    setFormData(prev => ({ ...prev, items: newItems }))
  }

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items]
    newItems[index][field] = value
    setFormData(prev => ({ ...prev, items: newItems }))
  }

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0
      return sum + amount
    }, 0)
  }

  const handleShowPreview = (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    // Validate
    const hasEmptyItems = formData.items.some(item => !item.particular || !item.amount)
    if (hasEmptyItems) {
      setError('All items must have particulars and amount')
      return
    }

    // Show preview modal (client-side only, no API call)
    const totalAmount = formData.items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0
      return sum + amount
    }, 0)

    setPreviewData({
      receipt_date: formData.receipt_date,
      receipt_time: formData.receipt_time,
      items: formData.items,
      narration: formData.narration,
      total_amount: totalAmount
    })
    setShowPreview(true)
  }

  const handleConfirmAndGenerate = async (receiptId, updatedData) => {
    try {
      setLoading(true)
      setError('')

      // Single API call - saves and generates PDF
      const response = await apiClient.post(
        `/insurance/patients/${patient.patient_id || patient.personal_id}/generate-receipt`,
        updatedData,
        { responseType: 'blob' }
      )

      // Open PDF in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      // Clean up after a delay to ensure the PDF loads
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)

      setSuccess('Receipt generated and opened in new tab!')

      // Close preview modal
      setShowPreview(false)
      setPreviewData(null)
      
      // Reset form
      setTimeout(() => {
        setFormData({
          receipt_date: new Date().toISOString().split('T')[0],
          receipt_time: new Date().toTimeString().slice(0, 5),
          items: [{ sl_no: 1, particular: 'OP', amount: '' }],
          narration: `${patient.uhid_no || ''} ${patient.full_name || ''}`.trim()
        })
        setIsOpen(false)
        setSuccess('')
      }, 2000)

    } catch (err) {
      console.error('Receipt generation error:', err)
      const errorMessage = err.response?.data?.error || err.message || 'Failed to generate receipt'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = handleShowPreview

  if (!patient) return null

  const totalAmount = calculateTotal()

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {/* <Receipt className="text-blue-600" size={24} /> */}
          <h2 className="text-xl font-bold text-gray-800">Generate Receipt</h2>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            isOpen
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isOpen ? 'Close' : 'Open Receipt Form'}
        </button>
      </div>

      {/* Collapsible Form */}
      {isOpen && (
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Alert Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {/* Static Info Display */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Receipt Information</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium">Patient:</span> {patient.full_name}</div>
              <div><span className="font-medium">UHID:</span> {patient.uhid_no}</div>
              <div><span className="font-medium">Center:</span> {patient.center_code}</div>
              <div><span className="font-medium">Mobile:</span> {patient.mobile}</div>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.receipt_date}
                onChange={(e) => setFormData({ ...formData, receipt_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.receipt_time}
                onChange={(e) => setFormData({ ...formData, receipt_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Items Table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Receipt Items <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <Plus size={16} />
                <span>Add Item</span>
              </button>
            </div>

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 w-16">SI No</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Particulars</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 w-40">Amount</th>
                    <th className="px-4 py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {formData.items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-center text-gray-700">{item.sl_no}</td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={item.particular}
                          onChange={(e) => updateItem(index, 'particular', e.target.value)}
                          placeholder="e.g., OP, Consultation, Treatment"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.amount}
                          onChange={(e) => updateItem(index, 'amount', e.target.value)}
                          placeholder="0.00"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-700"
                            title="Remove item"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-bold">
                    <td colSpan="2" className="px-4 py-3 text-right">Total Amount:</td>
                    <td className="px-4 py-3">{totalAmount.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Narration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Narration
            </label>
            <input
              type="text"
              value={formData.narration}
              onChange={(e) => setFormData({ ...formData, narration: e.target.value })}
              placeholder="Patient ID and Name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || totalAmount === 0}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Download size={18} />
                  <span>Review & Generate</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Receipt Preview Modal */}
      {showPreview && previewData && (
        <ReceiptEditor
          receipt={{
            receipt_no: 'NEW',
            receipt_id: null,
            receipt_date: previewData.receipt_date,
            receipt_time: previewData.receipt_time,
            items: previewData.items,
            narration: previewData.narration,
            total_amount: previewData.total_amount
          }}
          patient={patient}
          isPreview={true}
          onClose={() => {
            setShowPreview(false)
            setPreviewData(null)
          }}
          onSave={handleConfirmAndGenerate}
        />
      )}
    </div>
  )
}

export default GenerateReceipt
